// RouteTransitionManager.jsx - Navigation transitions with loading bar
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useNavigationPreloader from '../hooks/useNavigationPreloader';
import { takeArriveAtTop, holdAtTop } from '../utils/arrival';

const RouteTransitionManager = ({
  children,
  preloadedData,
  exploreDataReady,
  preloadExploreData
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const nextPageRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const navigationIdRef = useRef(0);

  // Loading bar state
  const [showLoadingBar, setShowLoadingBar] = useState(false);
  const [loadingBarVisible, setLoadingBarVisible] = useState(false);
  const [loadingBarKey, setLoadingBarKey] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Initialize navigation preloader hook
  const {
    preloadForNavigation,
    cancelPreload,
    isPreloading,
    preloadProgress,
  } = useNavigationPreloader(preloadedData, exploreDataReady, preloadExploreData);

  // Main pages that should show loading bar
  const MAIN_PAGES = ['/courses', '/lessons', '/problems'];

  // Check if a path is a main page
  const isMainPage = (path) => {
    return MAIN_PAGES.includes(path) ||
           path === '/lessons/new' ||
           path === '/problems/new' ||
           path === '/courses/new';
  };

  // Check if a container's content is fully loaded
  const isPageReady = useCallback((container, attempts = 0) => {
    if (!container) return false;

    // Check all images - but with blob URLs they should be instant
    const images = container.querySelectorAll('img');
    let pendingImages = 0;

    for (const img of images) {
      // Skip hidden images
      const style = window.getComputedStyle(img);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (img.dataset.loadFailed === 'true') continue;
      if (!img.parentNode) continue;

      // Check if image is loaded
      if (!img.complete || (img.naturalHeight === 0 && img.src)) {
        // For blob URLs, they should load instantly
        if (img.src && img.src.startsWith('blob:')) {
          continue; // Blob URLs are instant
        }
        // For API thumbnails, be more aggressive with timeout
        if (attempts > 3) {
          img.dataset.loadFailed = 'true';
          continue;
        }
        pendingImages++;
      }
    }

    if (pendingImages > 0) return false;

    // Check for loading spinners
    const loaders = container.querySelectorAll('[class*="animate-spin"], [class*="loading"]');
    if (loaders.length > 0) return false;

    // Check if content has minimum height
    const mainContent = container.querySelector('main, [role="main"], .container, .max-w-7xl');
    if (!mainContent || mainContent.offsetHeight < 100) return false;

    return true;
  }, []);

  // Handle navigation intercept
  useEffect(() => {
    const originalNavigate = window.__originalNavigate || navigate;
    if (!window.__originalNavigate) {
      window.__originalNavigate = navigate;
    }

    window.__navigateWithTransition = async (to, options) => {
      console.log('🚀 Navigation request:', to);

      // Determine if we should show loading bar
      const shouldShowLoadingBar =
        typeof to === 'string' &&
        to !== location.pathname &&
        !to.includes('/credits') &&
        !options?.replace &&
        !options?.skipTransition &&
        isMainPage(to) &&
        !to.includes('/courses/') && // Skip course details
        !to.includes('/video/'); // Skip video player

      if (shouldShowLoadingBar) {
        console.log('✨ Navigating with loading bar:', to);

        // Cancel any in-progress navigation
        if (checkIntervalRef.current) {
          console.log('🚫 Cancelling previous navigation for new destination:', to);
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        cancelPreload();

        // Increment navigation ID to invalidate stale callbacks
        navigationIdRef.current += 1;

        const transitionStart = performance.now();
        window.__lastTransitionStart = transitionStart;

        // Start loading bar
        setLoadingBarKey(prev => prev + 1);
        setShowLoadingBar(true);
        setLoadingBarVisible(false);
        setLoadingProgress(0);

        requestAnimationFrame(() => {
          setLoadingBarVisible(true);
        });

        // Start preloading thumbnails as blobs
        preloadForNavigation(to);

        setIsTransitioning(true);
        setNextPath(to);
      } else {
        console.log('⚡ Instant navigation:', to);
        originalNavigate(to, options);
      }
    };

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [location.pathname, navigate, preloadForNavigation, cancelPreload]);

  // Sync preload progress with loading bar
  useEffect(() => {
    if (isPreloading && showLoadingBar) {
      setLoadingProgress(preloadProgress);
    }
  }, [isPreloading, preloadProgress, showLoadingBar]);

  // Monitor next page readiness
  useEffect(() => {
    // Don't return early for null ref - the ref will be populated after React renders
    // the hidden page. We need to set up the interval to wait for it.
    if (!isTransitioning || !nextPath) return;

    // Capture navigation ID for this effect instance
    const navId = navigationIdRef.current;

    let attempts = 0;
    const maxAttempts = 30; // 1.5 seconds max
    const cleanupTimeouts = [];

    const checkReadiness = () => {
      // Abort if this navigation was superseded by a newer one
      if (navId !== navigationIdRef.current) {
        console.log(`🚫 Stale navigation cancelled (navId ${navId} !== current ${navigationIdRef.current})`);
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        return;
      }

      attempts++;

      // If ref isn't ready yet, continue waiting (React hasn't rendered the hidden page yet)
      if (!nextPageRef.current) {
        if (attempts >= maxAttempts) {
          // Timeout - navigate anyway even without ref
          console.log(`⏱️ Timeout waiting for ref, navigating to ${nextPath}`);
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
          }

          setLoadingProgress(100);
          const fadeTimeout = setTimeout(() => {
            setLoadingBarVisible(false);
            const hideTimeout = setTimeout(() => {
              setShowLoadingBar(false);
              setLoadingProgress(0);
            }, 200);
            cleanupTimeouts.push(hideTimeout);
          }, 200);
          cleanupTimeouts.push(fadeTimeout);

          cancelPreload();
          setCurrentPath(nextPath);
          setIsTransitioning(false);
          setNextPath(null);

          if (window.__originalNavigate) {
            window.__originalNavigate(nextPath);
          }
        }
        return;
      }

      const isReady = isPageReady(nextPageRef.current, attempts);
      const timedOut = attempts >= maxAttempts;

      if (isReady || timedOut) {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }

        const loadTime = window.__lastTransitionStart
          ? performance.now() - window.__lastTransitionStart
          : 0;

        console.log(`✅ Page ready for ${nextPath} in ${loadTime.toFixed(0)}ms`);

        // Complete loading bar
        setLoadingProgress(100);

        const fadeTimeout = setTimeout(() => {
          setLoadingBarVisible(false);
          const hideTimeout = setTimeout(() => {
            setShowLoadingBar(false);
            setLoadingProgress(0);
          }, 200);
          cleanupTimeouts.push(hideTimeout);
        }, 200);
        cleanupTimeouts.push(fadeTimeout);

        cancelPreload();
        setCurrentPath(nextPath);
        setIsTransitioning(false);
        setNextPath(null);

        if (window.__originalNavigate) {
          window.__originalNavigate(nextPath);
        }
      }
    };

    checkReadiness();
    checkIntervalRef.current = setInterval(checkReadiness, 50);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      cleanupTimeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [isTransitioning, nextPath, isPageReady, cancelPreload]);

  // Update current path when location changes (browser back/forward)
  useEffect(() => {
    if (!isTransitioning && location.pathname !== currentPath) {
      setCurrentPath(location.pathname);
    }
  }, [location.pathname, currentPath, isTransitioning]);

  /* Scroll to top on route change — except on the pages you were BROWSING.
   *
   * Workspace and Explore are long scrolling surfaces you read through; losing your place
   * means scrolling back past everything you already looked at. Home is a launchpad — the
   * composer sits at the top and arriving there almost always means starting something new —
   * so it still resets, deliberately.
   *
   * Browsers restore scroll on back/forward by default, so RESETTING is the behaviour that
   * needs a reason. Home has one. The other two did not.
   *
   * PER-PATH OFFSETS, not one shared value. All three tabs are mounted-and-hidden rather than
   * unmounted, so they share a single window scroll. Simply skipping the reset between them
   * was tried and is worse: Home then sits at whatever offset the Workspace had.
   *
   * The restore RETRIES across frames instead of scrolling once. While hidden a tab's wrapper
   * is `height: 0`, and the document does not regain its full height in the frame the tab is
   * revealed, so a single scrollTo would clamp to 0. It stops the moment the offset holds, so
   * it cannot fight a user who scrolls during the window.
   */
  const RESTORE_PATHS = ['/workspace', '/explore'];
  const offsetsRef = useRef({});

  useEffect(() => {
    const path = location.pathname;
    if (!RESTORE_PATHS.includes(path)) return;
    const onScroll = () => { offsetsRef.current[path] = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Detached before the reset effect below runs on the next navigation (hooks run in
    // declaration order), so leaving never overwrites the captured offset with 0.
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    /* NEW WORK LANDS AT THE TOP. Generating from Home navigates here, and restoring the offset
       you last read at drops you below the card you just made — see utils/arrival. Consumed
       unconditionally so the mark can never survive into a later navigation. */
    const isNewWork = takeArriveAtTop();
    const target = !isNewWork && RESTORE_PATHS.includes(path)
      ? (offsetsRef.current[path] || 0)
      : 0;
    if (target <= 0) {
      window.scrollTo(0, 0);
      /* AND KEEP IT THERE. Safari expands its toolbars ~86ms after the arrival and scrolls the
         document to compensate — measured; see utils/arrival. This reset has already finished
         by then, so it has to be held.
         EVERY top-landing arrival, not just new work. Scoping it to the generate cut was the
         bug on the video page: that route never got the hold, so the 34px shift stuck. The
         hold answers viewport events rather than polling, and releases on the first touch, so
         applying it broadly costs nothing and cannot sit on a deliberate scroll. */
      return holdAtTop();
    }
    let raf = 0;
    const deadline = performance.now() + 600;
    const settle = () => {
      window.scrollTo(0, target);
      if (Math.abs(window.scrollY - target) <= 2 || performance.now() > deadline) return;
      raf = requestAnimationFrame(settle);
    };
    raf = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  return (
    <div className="route-transition-container relative">
      {/* Loading Bar - YouTube style at the top */}
      {showLoadingBar && (
        <div
          key={`loading-bar-${loadingBarKey}`}
          className={`fixed top-0 left-0 right-0 h-1 z-[99999] transition-opacity duration-200 ${
            loadingBarVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ pointerEvents: 'none' }}
        >
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/50"
            style={{
              width: `${loadingProgress}%`,
              transition: loadingBarVisible ? 'width 300ms ease-out' : 'none',
              transformOrigin: 'left'
            }}
          />
        </div>
      )}

      {/* Current Page */}
      <div className="relative z-10">
        {React.cloneElement(children, {
          key: currentPath,
          location: { ...location, pathname: currentPath }
        })}
      </div>

      {/* Pre-rendered page (hidden) */}
      {isTransitioning && nextPath && isMainPage(nextPath) && (
        <div
          ref={nextPageRef}
          className="absolute inset-0 pointer-events-none"
          style={{ visibility: 'hidden', zIndex: -1 }}
        >
          {React.cloneElement(children, {
            key: nextPath,
            location: { ...location, pathname: nextPath }
          })}
        </div>
      )}
    </div>
  );
};

export default RouteTransitionManager;
